import { useState } from 'react'
import { suggestPassword } from '../../account/password'
import { ApiError } from '../../api/errors'
import { firstName, possessive, type User } from '../../api/users'
import { Button } from '../ui/Button'
import { TextField } from '../ui/TextField'
import { SettingEditButton, SettingForm, SettingRow, useSettingEditor } from './Setting'
import { useUpdateUser } from './useUpdateUser'

interface PasswordSettingProps {
  user: User
  /**
   * The signed-in parent's own password, which they change by proving they
   * know the current one — or a child's, which the parent resets outright,
   * because the parent is the recovery path for a child who has forgotten it.
   */
  own: boolean
  testId: string
}

/**
 * The password row. Its editor differs by whose password it is: a parent's
 * is typed twice, masked, after the current one. A child's opens on a
 * suggested password shown in the clear, since the parent has to read it out
 * anyway — and a visible value needs no confirmation field.
 */
export function PasswordSetting({ user, own, testId }: PasswordSettingProps) {
  const update = useUpdateUser(user)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  const editor = useSettingEditor({
    save: () => update.mutateAsync({ password: next, currentPassword: own ? current : undefined }),
    validate: () => {
      if (own && current === '') return 'Enter your current password.'
      if (next === '') return 'Enter a new password.'
      if (own && next !== confirm) return 'Those passwords don’t match.'
      return null
    },
    // The platform refuses a wrong current password as it would a stranger:
    // a plain 401, with nothing to point at. In this row, that is what it means.
    onError: (cause) => {
      if (!own || !(cause instanceof ApiError && cause.status === 401)) return null
      setCurrent('')
      return 'That isn’t your current password.'
    },
  })

  function start() {
    setCurrent('')
    setConfirm('')
    setNext(own ? '' : suggestPassword())
    editor.start()
  }

  if (!editor.open) {
    return (
      <div data-testid={testId}>
        <SettingRow
          label="Password"
          value={<span aria-hidden="true">••••••••</span>}
          action={
            <SettingEditButton
              editor={editor}
              onClick={start}
              aria-label={
                own ? 'Change your password' : `Reset ${possessive(firstName(user))} password`
              }
            >
              {own ? 'Change' : 'Reset'}
            </SettingEditButton>
          }
        />
      </div>
    )
  }

  if (own) {
    return (
      <div data-testid={testId}>
        <SettingForm editor={editor} submitLabel="Change password">
          <TextField
            label="Current password"
            labelStyle="floating"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            data-testid="password-current"
          />
          <TextField
            label="New password"
            labelStyle="floating"
            type="password"
            autoComplete="new-password"
            required
            value={next}
            onChange={(event) => setNext(event.target.value)}
            data-testid="password-new"
          />
          <TextField
            label="Confirm new password"
            labelStyle="floating"
            type="password"
            autoComplete="new-password"
            required
            invalid={editor.error !== null}
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            data-testid="password-confirm"
          />
        </SettingForm>
      </div>
    )
  }

  return (
    <div data-testid={testId}>
      <SettingForm
        editor={editor}
        submitLabel="Set password"
        note={`Write this down for ${firstName(user)}. We never email it.`}
      >
        <div className="flex items-end gap-3">
          <div className="min-w-0 flex-1">
            <TextField
              label="New password"
              labelStyle="floating"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              invalid={editor.error !== null}
              value={next}
              onChange={(event) => setNext(event.target.value)}
              data-testid="password-new"
            />
          </div>
          <Button
            variant="secondary"
            onClick={() => setNext(suggestPassword())}
            data-testid="password-suggest"
          >
            Suggest another
          </Button>
        </div>
      </SettingForm>
    </div>
  )
}
